import { DotsThree, Plus, UsersThree } from '@phosphor-icons/react';
import type { PersonaDto } from '@reelplicate/api-client/model';
import {
  getListPersonasQueryKey,
  useDeletePersona,
  useListPersonas,
} from '@reelplicate/api-client/personas';
import { getListSocialAccountsQueryKey } from '@reelplicate/api-client/social-accounts';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Inline,
  Link,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Skeleton,
  Stack,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import {
  SettingsCard,
  SettingsPageLayout,
  SettingsSection,
} from '@/features/settings/settings-layout';

import { PersonaAccounts } from './persona-accounts';
import { PersonaModal } from './persona-modal';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "Bea · 2 accounts": the short name (when set) and how many accounts post as them. */
const personaMeta = (p: PersonaDto) =>
  [p.shortName, plural(p.accountCount, 'account')].filter(Boolean).join(' · ');

/**
 * Personas (sidebar): the faces the brand posts as, each with its own
 * accounts and voice. How many a workspace can have depends on its plan;
 * past the allowance Add is disabled and points at Billing.
 */
export function PersonasPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const { data } = useListPersonas(workspaceId, {
    query: { enabled: Boolean(workspace) },
  });
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<PersonaDto | null>(null);
  const queryClient = useQueryClient();
  const remove = useDeletePersona();

  const removePersona = async (persona: PersonaDto) => {
    try {
      await remove.mutateAsync({ id: workspaceId, personaId: persona.id });
    } catch {
      return; // The failure is already shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListPersonasQueryKey(workspaceId) }),
      queryClient.invalidateQueries({
        queryKey: getListSocialAccountsQueryKey(workspaceId),
      }),
    ]);
    toast.success(`${persona.name} deleted`);
  };

  const full = data ? data.used >= data.limit : true;
  const add = (
    <Button leadingIcon={Plus} disabled={full} onClick={() => setAdding(true)}>
      Add persona
    </Button>
  );

  return (
    <SettingsPageLayout
      title="Personas"
      showBack={false}
      description="Faces your brand posts as - each with their own accounts, voice and reference look."
      actions={add}
    >
      {data && full && (
        <Well
          tone="info"
          title={
            data.limit === 0
              ? 'Your plan doesn’t include personas'
              : `You’re using ${data.used} of ${plural(data.limit, 'persona')}`
          }
        >
          <Link asChild size="sm">
            <RouterLink to="/settings/billing">Upgrade your plan</RouterLink>
          </Link>{' '}
          to add {data.limit === 0 ? 'one' : 'more'}.
        </Well>
      )}

      <SettingsSection
        title="Your personas"
        description={
          data
            ? `${data.used} of ${plural(data.limit, 'persona')} on your plan.`
            : undefined
        }
      >
        {!data ? (
          <SettingsCard>
            <Skeleton className="my-4 h-10" />
          </SettingsCard>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={UsersThree}
            title="No personas yet"
            description="Add a persona to publish as someone other than the brand, like a barista or a regular."
          />
        ) : (
          <Stack gap={4}>
            {data.items.map((persona) => (
              <SettingsCard key={persona.id}>
                <div className="py-5">
                  <Inline className="gap-3.5 pb-1.5">
                    <Avatar
                      size={40}
                      name={persona.name}
                      src={persona.avatarUrl ?? undefined}
                      alt=""
                    />
                    <Stack gap={0.5} className="min-w-0 flex-1">
                      <p className="text-body-lg-strong text-text-primary truncate">
                        {persona.name}
                      </p>
                      <p className="text-caption text-text-secondary truncate">
                        {personaMeta(persona)}
                      </p>
                    </Stack>
                    <Badge
                      size="sm"
                      tone={
                        persona.referenceCount === 5
                          ? 'success'
                          : persona.referenceCount
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {persona.referenceCount
                        ? `Reference look · ${persona.referenceCount} of 5`
                        : 'No reference look'}
                    </Badge>
                    <Button asChild size="sm" variant="outline">
                      <RouterLink to={`/personas/${persona.id}`}>Edit</RouterLink>
                    </Button>
                    <Menu>
                      <MenuTrigger asChild>
                        <IconButton
                          icon={DotsThree}
                          size="sm"
                          variant="ghost"
                          aria-label={`More for ${persona.name}`}
                        />
                      </MenuTrigger>
                      <MenuContent align="end">
                        <MenuItem type="danger" onSelect={() => setDeleting(persona)}>
                          Delete persona
                        </MenuItem>
                      </MenuContent>
                    </Menu>
                  </Inline>
                  <PersonaAccounts
                    workspaceId={workspaceId}
                    persona={persona}
                    canEdit={false}
                  />
                </div>
              </SettingsCard>
            ))}
          </Stack>
        )}
      </SettingsSection>

      {workspace && (
        <PersonaModal
          workspaceId={workspace.id}
          uploadsEnabled={workspace.uploadsEnabled}
          open={adding}
          onOpenChange={setAdding}
          onCreated={(persona) => void navigate(`/personas/${persona.id}`)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'persona'}?`}
        description={
          deleting?.accountCount
            ? `Its ${plural(deleting.accountCount, 'connected account')} will be removed too. This can’t be undone.`
            : 'This can’t be undone.'
        }
        confirmLabel="Delete persona"
        onConfirm={() => deleting && void removePersona(deleting)}
      />
    </SettingsPageLayout>
  );
}
